// Lightbox organism — accessible fullscreen image gallery for admin views.
import { useCallback, useEffect, useRef, useState } from "react";
import { resolveImageUrl } from "../../utils/imageUrl";

const PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23e5e7eb' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='12' fill='%23999'%3EImage Error%3C/text%3E%3C/svg%3E";

export const Lightbox = ({
  images = [],
  startIndex = 0,
  isOpen,
  onClose,
  alt,
}) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const overlayRef = useRef(null);
  const previouslyFocused = useRef(null);

  const count = images.length;
  const hasMultiple = count > 1;

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + count) % count);
  }, [count]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % count);
  }, [count]);

  // Sync index when (re)opened.
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(startIndex);
    }
  }, [isOpen, startIndex]);

  // Keyboard, scroll lock, focus management.
  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocused.current = document.activeElement;
    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    // Move focus into the dialog.
    overlayRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft" && hasMultiple) {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight" && hasMultiple) {
        e.preventDefault();
        goNext();
      } else if (e.key === "Tab") {
        // Focus trap: keep focus on the overlay container.
        e.preventDefault();
        overlayRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      root.style.overflow = prevOverflow;
      // Restore focus to the trigger.
      if (previouslyFocused.current instanceof HTMLElement) {
        previouslyFocused.current.focus();
      }
    };
  }, [isOpen, hasMultiple, goPrev, goNext, onClose]);

  if (!isOpen || count === 0) {
    return null;
  }

  // Clamp in case the images array shrank or startIndex was out of range.
  const safeIndex = Math.min(Math.max(currentIndex, 0), count - 1);
  const src = resolveImageUrl(images[safeIndex]);
  const altText = alt ? alt(safeIndex) : `Image ${safeIndex + 1}`;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      tabIndex={-1}
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4 outline-none"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close gallery"
        className="absolute top-6 right-8 text-white text-4xl font-bold hover:text-gray-300"
      >
        ×
      </button>

      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          aria-label="Previous image"
          className="absolute left-4 md:left-8 text-white text-5xl font-bold hover:text-gray-300 select-none"
        >
          ‹
        </button>
      )}

      <div
        className="flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={altText}
          className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain"
          onError={(e) => {
            e.target.src = PLACEHOLDER;
          }}
        />
        {hasMultiple && (
          <p className="mt-4 text-white text-sm" aria-live="polite">
            {safeIndex + 1} / {count}
          </p>
        )}
      </div>

      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          aria-label="Next image"
          className="absolute right-4 md:right-8 text-white text-5xl font-bold hover:text-gray-300 select-none"
        >
          ›
        </button>
      )}
    </div>
  );
};
